import type {
  CoreAssistantMessage,
  CoreToolMessage,
  Message,
  TextStreamPart,
  ToolInvocation,
  ToolSet,
  UIMessage,
} from 'ai';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import type { DBMessage, Document } from '@/lib/db/schema';
import { RagSource } from '@/lib/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ApplicationError extends Error {
  info: string;
  status: number;
}

export const fetcher = async (url: string) => {
  const res = await fetch(url);

  if (!res.ok) {
    const error = new Error(
      'An error occurred while fetching the data.',
    ) as ApplicationError;

    error.info = await res.json();
    error.status = res.status;

    throw error;
  }

  return res.json();
};

export function getLocalStorage(key: string) {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem(key) || '[]');
  }
  return [];
}

export function generateUUID(): string {
  // RFC4122 UUID v4 implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function addToolMessageToChat({
  toolMessage,
  messages,
}: {
  toolMessage: CoreToolMessage;
  messages: Array<Message>;
}): Array<Message> {
  return messages.map((message) => {
    if (message.toolInvocations) {
      return {
        ...message,
        toolInvocations: message.toolInvocations.map((toolInvocation) => {
          const toolResult = toolMessage.content.find(
            (tool) => tool.toolCallId === toolInvocation.toolCallId,
          );

          if (toolResult) {
            return {
              ...toolInvocation,
              state: 'result',
              result: toolResult.result,
            };
          }

          return toolInvocation;
        }),
      };
    }

    return message;
  });
}

type ResponseMessageWithoutId = CoreToolMessage | CoreAssistantMessage;
type ResponseMessage = ResponseMessageWithoutId & { id: string };

export function sanitizeResponseMessages({
  messages,
  reasoning,
}: {
  messages: Array<ResponseMessage>;
  reasoning: string | undefined;
}) {
  const toolResultIds: Array<string> = [];

  for (const message of messages) {
    if (message.role === 'tool') {
      for (const content of message.content) {
        if (content.type === 'tool-result') {
          toolResultIds.push(content.toolCallId);
        }
      }
    }
  }

  const messagesBySanitizedContent = messages.map((message) => {
    if (message.role !== 'assistant') return message;

    if (typeof message.content === 'string') return message;

    const sanitizedContent = message.content.filter((content) =>
      content.type === 'tool-call'
        ? toolResultIds.includes(content.toolCallId)
        : content.type === 'text'
          ? content.text.length > 0
          : true,
    );

    if (reasoning) {
      // @ts-expect-error: reasoning message parts in sdk is wip
      sanitizedContent.push({ type: 'reasoning', reasoning });
    }

    return {
      ...message,
      content: sanitizedContent,
    };
  });

  return messagesBySanitizedContent.filter(
    (message) => message.content.length > 0,
  );
}

export function getMostRecentUserMessage(messages: Array<UIMessage>) {
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.at(-1);
}

export function getDocumentTimestampByIndex(
  documents: Array<Document>,
  index: number,
) {
  if (!documents) return new Date();
  if (index > documents.length) return new Date();

  return documents[index].createdAt;
}

export function getTrailingMessageId({
  messages,
}: {
  messages: Array<ResponseMessage>;
}): string | null {
  const trailingMessage = messages.at(-1);

  if (!trailingMessage) return null;

  return trailingMessage.id;
}

/**
 * Format an array of documents into a single context string for the LLM.
 * 
 * @param docs - Array of documents retrieved from the database
 * @returns A formatted context string suitable for sending to the LLM
 */
export function formatDocumentsToContext(docs: Document[]): string {
  if (!docs || docs.length === 0) {
    return 'No relevant documents found.';
  }

  // Sort documents by similarity score if available
  const sortedDocs = [...docs].sort((a, b) => {
    // Higher similarity is better
    return (b.similarity ?? 0) - (a.similarity ?? 0);
  });

  // Build the context string with document separators and metadata
  return sortedDocs.map((doc, index) => {
    let docContext = `--- Document ${index + 1}: ${doc.title} ---\n`;
    
    // Add URL if available
    if (doc.url) {
      docContext += `Source: ${doc.url}\n`;
    }
    
    // Add similarity score if available (useful for debugging)
    if (doc.similarity !== undefined) {
      const similarityPercent = Math.round(doc.similarity * 100);
      docContext += `Relevance: ${similarityPercent}%\n`;
    }
    
    // Add the document content
    docContext += `\n${doc.content}\n`;
    
    return docContext;
  }).join('\n\n');
}

export interface Source {
  title: string;
  url: string;
  content: string;
}

/**
 * Extract unique sources from a list of documents
 */
export function extractSources(documents: any[]): Source[] {
  // Use Map to ensure unique sources by URL
  const uniqueSources = new Map<string, Source>();
  
  for (const doc of documents) {
    if (doc.url && !uniqueSources.has(doc.url)) {
      uniqueSources.set(doc.url, {
        title: doc.title || 'Source Document',
        url: doc.url,
        content: doc.content || ''
      });
    }
  }
  
  // Convert Map values to array
  return Array.from(uniqueSources.values());
}
